import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { motion } from 'framer-motion';
import { Eye, EyeOff, Brain, Loader2, AlertCircle } from 'lucide-react';
import { useState, useRef } from 'react';
import api from '@/api/client';
import { useAuthStore } from '@/store/authStore';
import Turnstile, { captchaEnabled, type TurnstileHandle } from '@/components/Turnstile';
import { postLoginDestination } from '@/lib/navigation';
import { isCaptchaRefusal } from '@/lib/loginErrors';
import toast from 'react-hot-toast';

const schema = z.object({
  email: z.string().email('Enter a valid email'),
  password: z.string().min(1, 'Password is required'),
  rememberMe: z.boolean().optional(),
});
type FormValues = z.infer<typeof schema>;

export default function LoginPage() {
  const [showPass, setShowPass] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');
  // The captcha stays out of the way until the server tells us this IP or email has
  // failed enough times to warrant it.
  const [needsCaptcha, setNeedsCaptcha] = useState(false);
  const turnstileRef = useRef<TurnstileHandle>(null);
  const { setAuth } = useAuthStore();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<FormValues>({
    resolver: zodResolver(schema),
  });

  const onSubmit = async (data: FormValues) => {
    try {
      const res = await api.post('/auth/login', { ...data, captchaToken });
      const { accessToken, user } = res.data.data;
      setAuth(user, accessToken);
      toast.success(`Welcome back, ${user.firstName}!`);

      // Come back to whatever page the expired session interrupted, so a timeout
      // doesn't also cost the user their place. postLoginDestination refuses
      // anything that could navigate off-site — see lib/navigation.ts — and, given the
      // role, anywhere that role may not open (an Interviewer lands on My interviews).
      navigate(postLoginDestination(searchParams.toString(), user.role));
    } catch (err: any) {
      toast.error(err.response?.data?.message ?? 'Login failed');

      // Reveal the widget whenever the server refuses for a captcha reason — see
      // lib/loginErrors.ts for why the explicit flag alone isn't sufficient during
      // a deploy, when the SPA and API are briefly running different versions.
      if (isCaptchaRefusal(err)) setNeedsCaptcha(true);

      // The Turnstile token was consumed by this attempt; recycle it so a retry
      // gets a fresh one instead of a rejected `timeout-or-duplicate`.
      setCaptchaToken('');
      turnstileRef.current?.reset();
    }
  };

  return (
    <div className="min-h-screen flex bg-surface">
      {/* Left hero panel */}
      <div className="hidden lg:flex lg:flex-1 bg-gradient-brand relative overflow-hidden items-center justify-center p-12">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          className="relative z-10 text-center"
        >
          <div className="w-24 h-24 rounded-3xl bg-white/10 flex items-center justify-center mx-auto mb-8 border border-white/20">
            <Brain className="w-12 h-12 text-white" />
          </div>
          <h1 className="font-display text-5xl font-bold text-white mb-4">
            CogniTest
          </h1>
          <p className="text-white/80 text-lg max-w-sm mx-auto leading-relaxed">
            The enterprise-grade platform for cognitive, technical, and custom assessments.
          </p>

          <div className="mt-12 grid grid-cols-3 gap-4">
            {[
              { label: 'Question Types', value: '9+' },
              { label: 'Anti-Cheat Checks', value: '8+' },
              { label: 'Assessment Types', value: '7+' },
            ].map((stat) => (
              <div key={stat.label} className="bg-white/10 rounded-2xl p-4 border border-white/10">
                <div className="text-2xl font-bold text-white">{stat.value}</div>
                <div className="text-xs text-white/70 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Right login panel */}
      <div className="flex-1 lg:max-w-md flex items-center justify-center p-8 bg-surface">
        <motion.div
          initial={{ opacity: 0, x: 24 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-sm"
        >
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-3 mb-10">
            <div className="w-10 h-10 rounded-xl bg-primary-500 flex items-center justify-center">
              <Brain className="w-5 h-5 text-white" />
            </div>
            <span className="font-display font-bold text-2xl text-gray-900">CogniTest</span>
          </div>

          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900">Welcome back</h2>
            <p className="text-gray-500 mt-1">Sign in to your admin account</p>
          </div>

          {/* noValidate hands validation to zod so the styled inline message shows,
              matching the apply and forgot-password forms instead of firing the
              browser's native bubble. */}
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" id="login-form" noValidate>
            {/* Email */}
            <div>
              <label className="form-label" htmlFor="email">Email address</label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                className="input"
                placeholder="admin@company.com"
                {...register('email')}
              />
              {errors.email && (
                <p className="form-error"><AlertCircle className="w-3.5 h-3.5" />{errors.email.message}</p>
              )}
            </div>

            {/* Password */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="form-label mb-0" htmlFor="password">Password</label>
                <Link
                  to="/auth/forgot-password"
                  className="text-xs text-primary-500 hover:text-primary-600 transition-colors"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <input
                  id="password"
                  type={showPass ? 'text' : 'password'}
                  autoComplete="current-password"
                  className="input pr-11"
                  placeholder="••••••••"
                  {...register('password')}
                />
                <button
                  type="button"
                  onClick={() => setShowPass(!showPass)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  aria-label="Toggle password visibility"
                >
                  {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {errors.password && (
                <p className="form-error"><AlertCircle className="w-3.5 h-3.5" />{errors.password.message}</p>
              )}
            </div>

            {/* Remember me */}
            <div className="flex items-center gap-2.5">
              <input
                id="rememberMe"
                type="checkbox"
                className="w-4 h-4 rounded border-gray-300 text-primary-500 focus:ring-primary-500"
                {...register('rememberMe')}
              />
              <label htmlFor="rememberMe" className="text-sm text-gray-600 cursor-pointer">
                Remember me for 30 days
              </label>
            </div>

            {/* Anti-bot. Adaptive: hidden for a normal sign-in, and shown once the
                server reports repeated failures for this IP or email. The server
                enforces the requirement independently, so this is presentation only. */}
            {captchaEnabled && needsCaptcha && (
              // Stable hook for tests. Asserting on the copy made the E2E check pass
              // vacuously the moment the wording changed.
              <div data-testid="captcha-block">
                {/* Wording has to hold for BOTH Turnstile modes. On production the
                    widget is in managed mode, which usually resolves invisibly — so
                    "confirm you're not a robot" would sit above nothing the user can
                    click. Cloudflare escalates to an interactive checkbox only when it
                    wants to, and this phrasing reads correctly either way. */}
                <p className="text-sm text-gray-700 mb-2 flex items-start gap-1.5">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>
                    One more step — we&apos;re running a quick security check.
                    It may finish on its own.
                  </span>
                </p>
                <Turnstile ref={turnstileRef} onVerify={setCaptchaToken} />
              </div>
            )}

            {/* Submit */}
            <button
              id="login-submit"
              type="submit"
              disabled={isSubmitting || (captchaEnabled && needsCaptcha && !captchaToken)}
              className="btn-primary w-full btn-lg"
            >
              {isSubmitting ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Signing in...</>
              ) : (
                'Sign in'
              )}
            </button>
          </form>
        </motion.div>
      </div>
    </div>
  );
}
